import { AlertCircle, Building2, RefreshCcw } from "lucide-react";

import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";

export interface ProjectTenantsProps {
  projectId: string;
  /** Navigate to a tenant's detail page. */
  onSelectTenant?: (id: string) => void;
}

export const ProjectTenants = ({ projectId, onSelectTenant }: ProjectTenantsProps) => {
  const { data, error, loading, refresh } = useCall("listProjectTenants", { projectId });
  const tenants = data?.tenants ?? [];

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={refresh}>
          <RefreshCcw />
          Refresh
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-10" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                </TableRow>
              ))
            ) : tenants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="p-0">
                  <EmptyState
                    icon={Building2}
                    title="No tenants yet"
                    description="No tenant has been created inside this product."
                    className="border-0 rounded-none bg-transparent"
                  />
                </TableCell>
              </TableRow>
            ) : (
              tenants.map((t) => (
                <TableRow
                  key={t.id}
                  className={onSelectTenant ? "cursor-pointer" : undefined}
                  onClick={onSelectTenant ? () => onSelectTenant(t.id) : undefined}
                >
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-xs font-semibold uppercase">
                        {t.name.slice(0, 2)}
                      </div>
                      <span className="font-medium">{t.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                  <TableCell>
                    <Badge variant="muted">{t.memberCount}</Badge>
                  </TableCell>
                  <TableCell title={t.createdAt} className="text-sm text-muted-foreground">
                    {formatRelative(t.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
